import { createTheme, Theme } from '@mui/material/styles';
import type {} from '@mui/lab/themeAugmentation';
import { blue } from '@mui/material/colors';
import type {} from '@mui/x-date-pickers/themeAugmentation';
import type {} from '@mui/x-data-grid-pro/themeAugmentation';
declare module '@mui/material/styles' {
  interface Palette {
    emerald: Palette['primary'];
    amber: Palette['primary'];
    lightBlue: Palette['primary'];
    salmon: Palette['primary'];
    green: Palette['primary'];
    semiBlack: Palette['primary'];
    lightGrey: Palette['primary'];
    mutedBlack: Palette['primary'];
    orange: Palette['primary'];
    red: Palette['primary'];
    paleRed: Palette['primary'];
    goldenYellow: Palette['primary'];
    darkRed: Palette['primary'];
    infoLight: Palette['primary'];
    infoText: Palette['primary'];
    infoIcon: Palette['primary'];
    errorDark: Palette['primary'];
    warningDark: Palette['primary'];
    successText: Palette['primary'];
    metallicBlue: Palette['primary'];
    selectedPageBg: Palette['primary'];
    hoverTableRowBg: Palette['primary'];
    selectedTableRowBg: Palette['primary'];
    selectedButton: Palette['primary'];
    headerBackground: Palette['primary'];
    blue: typeof blue;
  }

  interface PaletteOptions {
    emerald: PaletteOptions['primary'];
    amber?: PaletteOptions['primary'];
    lightBlue?: PaletteOptions['primary'];
    salmon?: PaletteOptions['primary'];
    green?: PaletteOptions['primary'];
    semiBlack?: PaletteOptions['primary'];
    lightGrey?: PaletteOptions['primary'];
    mutedBlack?: PaletteOptions['primary'];
    orange?: PaletteOptions['primary'];
    red?: PaletteOptions['primary'];
    paleRed?: PaletteOptions['primary'];
    goldenYellow?: PaletteOptions['primary'];
    darkRed?: PaletteOptions['primary'];
    infoLight?: PaletteOptions['primary'];
    infoText?: PaletteOptions['primary'];
    infoIcon?: PaletteOptions['primary'];
    errorDark?: PaletteOptions['primary'];
    warningDark?: PaletteOptions['primary'];
    successText?: PaletteOptions['primary'];
    metallicBlue?: PaletteOptions['primary'];
    selectedPageBg?: PaletteOptions['primary'];
    hoverTableRowBg?: PaletteOptions['primary'];
    selectedTableRowBg?: PaletteOptions['primary'];
    selectedButton?: PaletteOptions['primary'];
    headerBackground?: PaletteOptions['primary'];
    blue?: typeof blue;
  }
}

export const theme: Theme = createTheme({
  breakpoints: {
    values: {
      xs: 960,
      sm: 1200,
      md: 1536,
      lg: 1920,
      xl: 2200,
    },
  },
  palette: {
    blue,
    primary: {
      main: '#133F6E',
      light: '#BAD9E9',
    },
    secondary: {
      main: '#00A97A',
    },

    emerald: { main: '#71CDAF' },
    amber: {
      main: '#FFECB3',
    },
    lightBlue: {
      main: '#BBDEFB',
    },
    metallicBlue: {
      main: '#3D557611',
    },
    salmon: {
      main: '#FECDD2',
    },
    green: {
      main: '#C8E6C9',
    },
    goldenYellow: {
      main: '#F7C948',
    },
    red: {
      main: '#D32F2F',
    },
    darkRed: {
      main: '#5F2120',
    },
    infoLight: {
      main: '#E5F6FD',
    },
    infoText: {
      main: '#014361',
    },
    infoIcon: {
      main: '#0288D1',
    },
    errorDark: {
      main: '#C62828',
    },
    warningDark: {
      main: '#E65100',
    },
    successText: {
      main: '#1E4620',
    },
    paleRed: {
      main: '#FDEDED',
    },
    orange: {
      main: '#FF6E40',
    },
    success: {
      main: '#2E7D32',
      contrastText: '#1E4620',
    },
    error: {
      main: 'rgba(204, 16, 16, 0.87)',
    },
    warning: {
      main: '#EF6C00',
    },
    info: {
      main: 'rgba(2, 136, 209, 1)',
    },
    common: {
      white: '#FFF',
      black: '#000',
    },
    lightGrey: {
      main: '#00000014',
      dark: '#616161',
    },
    semiBlack: {
      main: '#00000099',
    },
    selectedPageBg: {
      main: 'rgba(19, 63, 110, 0.1)',
    },
    hoverTableRowBg: {
      main: '#133F6E1A',
    },
    selectedTableRowBg: {
      main: '#133F6E33',
    },
    selectedButton: {
      main: '#B8C5D3',
    },
    headerBackground: {
      main: '#EEEEEE',
    },
  },
  typography: {
    fontFamily: 'Poppins, sans-serif',
  },
  components: {
    MuiTabPanel: {
      defaultProps: {
        style: {
          padding: 0,
          paddingTop: '1rem',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        style: {
          borderRadius: 0,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&.MuiDatePicker-clearButton': {
            opacity: 0,
            transition: 'opacity 0.2s ease',
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            color: '#ff0000', // Disabled text color for all Buttons
          },
        },
      },
    },
    MuiInputBase: {
      defaultProps: {
        style: {
          borderRadius: 0,
        },
      },
      styleOverrides: {
        root: {
          '&:has(> input:-webkit-autofill)': {
            backgroundColor: '#e8f0fe',
          },
          '& .Mui-disabled': {
            backgroundColor: '#F5F5F5',

            ':hover': {
              cursor: 'not-allowed',
            },
          },
        },
        input: {
          padding: '0.5rem 0.75rem',
        },
      },
    },
    MuiOutlinedInput: {
      defaultProps: {
        style: {
          borderRadius: 0,
        },
      },
      styleOverrides: {
        root: {
          '&:has(> input:-webkit-autofill)': {
            backgroundColor: '#e8f0fe',
          },
          '& .Mui-disabled': {
            backgroundColor: '#F5F5F5',

            ':hover': {
              cursor: 'not-allowed',
            },
          },
        },
        notchedOutline: {},
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          '& .Mui-disabled': {
            backgroundColor: '#F5F5F5',

            ':hover': {
              cursor: 'not-allowed',
            },
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            background: 'rgba(19, 63, 110, 0.2)',
          },
          ':hover': {
            background: 'rgba(19, 63, 110, 0.1)',
          },
          ':active': {
            background: 'rgba(19, 63, 110, 0.3)',
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          ':disabled': {
            backgroundColor: '#FAFAFA',

            ':hover': {
              cursor: 'not-allowed',
            },
          },
          '& .Mui-disabled.MuiInputBase-root': {
            backgroundColor: '#F5F5F5',

            ':hover': {
              cursor: 'not-allowed',
            },
          },
          '& .Mui-disabled.MuiPickersInputBase-root': {
            backgroundColor: '#F5F5F5',

            ':hover': {
              cursor: 'not-allowed',
            },
          },
        },
      },
    },
    MuiTablePagination: {
      defaultProps: {
        style: {
          position: 'sticky',
          insetBlockEnd: 0,
          width: '100%',
        },
      },
    },
    MuiDatePicker: {
      defaultProps: {
        slotProps: {
          field: { clearable: true },
        },
      },
    },
    MuiPickersTextField: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.common.white,
        }),
      },
    },
    MuiLink: {
      defaultProps: {
        underline: 'always',
      },
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.primary.main,
          textDecorationColor: theme.palette.primary.dark,
          '&:hover': {
            color: theme.palette.primary.dark,
          },
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: '2px',
            borderRadius: '2px',
          },
        }),
      },
    },
    MuiDataGrid: {
      defaultProps: {
        sortingOrder: ['asc', 'desc'],
      },
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiDataGrid-iconButtonContainer .MuiIconButton-root': {
            backgroundColor: 'transparent',
            color: 'inherit',
          },

          '&& .MuiDataGrid-filler': {
            backgroundColor: theme.palette.headerBackground.main,
          },

          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },
          '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
            {
              outline: 'none',
            },
          '& .MuiDataGrid-columnHeader:has(.MuiDataGrid-menuOpen)': {
            backgroundColor: `rgba(19, 63, 110, 0.2)`,
            '& .MuiDataGrid-columnHeaderTitleContainer': {
              backgroundColor: 'transparent',
            },
          },

          '& .MuiDataGrid-row.row-even': {
            backgroundColor: theme.palette.grey[100],
          },
          '& .MuiDataGrid-cell.MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-cell.MuiDataGrid-cell--pinnedRight':
            {
              backgroundColor: theme.palette.common.white,
            },
          '& .MuiDataGrid-row.row-even .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-row.row-even .MuiDataGrid-cell--pinnedRight':
            {
              backgroundColor: theme.palette.grey[100],
            },
          '& .MuiDataGrid-row:hover, & .MuiDataGrid-row.row-even:hover': {
            backgroundColor: `rgba(19, 63, 110, 0.1)`,
          },
          '& .MuiDataGrid-row:hover .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-row:hover .MuiDataGrid-cell--pinnedRight':
            {
              backgroundColor: `rgb(233, 238, 245)`,
            },
          '& .MuiDataGrid-row.row-even:hover .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-row.row-even:hover .MuiDataGrid-cell--pinnedRight':
            {
              backgroundColor: `rgb(221, 228, 238)`,
            },
        }),
        columnHeader: ({ theme }) => ({
          backgroundColor: theme.palette.headerBackground.main,
        }),
        columnHeaders: ({ theme }) => ({
          backgroundColor: theme.palette.headerBackground.main,
        }),
        columnHeaderTitleContainer: ({ theme }) => ({
          backgroundColor: theme.palette.headerBackground.main,
        }),

        iconButtonContainer: {
          backgroundColor: 'transparent',
          color: 'inherit',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&:nth-of-type(even)': {
            backgroundColor: theme.palette.grey[100],
          },
        }),
      },
    },
  },
});
